import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Minus, Plus, Trash2, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { cart, removeFromCart, updateQuantity, cartTotal, clearCart } = useCart();
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [error, setError] = useState('');
  const [phone, setPhone] = useState('');

  const handleCheckout = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await axios.post(`${API}/orders/checkout`, {
        items: cart.map(item => ({ product_id: item.product_id || item.id, quantity: item.quantity, size: item.size })),
        customer_name: user ? `${user.first_name} ${user.last_name}` : 'Guest',
        customer_email: user?.email || '',
        customer_phone: phone,
        fulfillment: 'collection'
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Checkout failed');
    }
  };

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-uf-bg flex items-center justify-center">
        <div className="text-center">
          <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold text-white mb-2">Order Placed!</h2>
          <p className="text-uf-textMuted mb-6">Thanks for your purchase.</p>
          <Link to="/shop" className="bg-uf-red text-white px-6 py-3 rounded-xl font-semibold">Continue Shopping</Link>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-uf-bg flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-uf-textMuted mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold text-white mb-2">Your cart is empty</h2>
          <p className="text-uf-textMuted mb-6">Add items from the shop to get started.</p>
          <Link to="/shop" className="bg-uf-red text-white px-6 py-3 rounded-xl font-semibold">Browse Shop</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-uf-bg py-8" data-testid="cart-page">
      <section className="max-w-5xl mx-auto px-4">
        <h1 className="font-heading text-3xl font-bold text-white mb-8">Your Cart</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cart.map(item => {
              const pid = item.product_id || item.id;
              const name = item.product_name || item.name;
              const img = item.image || item.image_url;
              return (
                <div key={`${pid}-${item.size || ''}`} className="bg-uf-surface border border-uf-border rounded-xl p-4 flex gap-4" data-testid={`cart-item-${pid}`}>
                  {img ? <img src={img} alt={name} className="w-20 h-20 object-cover rounded-lg" /> : <div className="w-20 h-20 bg-zinc-800 rounded-lg flex items-center justify-center"><ShoppingBag className="w-6 h-6 text-zinc-600" /></div>}
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold text-white">{name}</h3>
                    {item.size && <p className="text-uf-textMuted text-sm">Size: {item.size}</p>}
                    {item.color && <p className="text-uf-textMuted text-sm">Colour: {item.color}</p>}
                    <p className="text-uf-red font-bold">£{item.price.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQuantity(pid, item.size, item.quantity - 1)} className="w-8 h-8 border border-uf-border rounded flex items-center justify-center hover:border-uf-red"><Minus size={16} /></button>
                    <span className="w-8 text-center text-white">{item.quantity}</span>
                    <button onClick={() => updateQuantity(pid, item.size, item.quantity + 1)} className="w-8 h-8 border border-uf-border rounded flex items-center justify-center hover:border-uf-red"><Plus size={16} /></button>
                    <button onClick={() => removeFromCart(pid, item.size)} className="ml-4 text-uf-textMuted hover:text-uf-red"><Trash2 size={20} /></button>
                  </div>
                </div>
              );
            })}
          </div>
          <div>
            <div className="bg-uf-surface border border-uf-border rounded-xl p-6" data-testid="cart-summary">
              <h2 className="font-heading text-xl font-bold text-white mb-4">Order Summary</h2>
              {cart.map(item => {
                const pid = item.product_id || item.id;
                const name = item.product_name || item.name;
                return (
                  <div key={`${pid}-${item.size || ''}`} className="flex justify-between text-sm text-uf-textMuted mb-2">
                    <span>{name} × {item.quantity}</span>
                    <span className="text-white">£{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                );
              })}
              <div className="border-t border-uf-border mt-4 pt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xl text-white">Total</span>
                  <span className="font-heading text-3xl font-bold text-uf-red">£{cartTotal.toFixed(2)}</span>
                </div>
                <p className="text-zinc-500 text-xs text-right mb-6">inc. VAT</p>
              </div>
              {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
              <form onSubmit={handleCheckout} className="space-y-4">
                {!isAuthenticated && (
                  <input type="tel" placeholder="Phone Number" value={phone} onChange={e => setPhone(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-zinc-500" data-testid="cart-phone" />
                )}
                <button type="submit" className="w-full py-3.5 bg-uf-red text-white rounded-xl font-heading font-bold text-lg hover:bg-uf-red/90 transition-all" data-testid="checkout-btn">
                  Proceed to Checkout
                </button>
              </form>
              <Link to="/shop" className="block text-center text-uf-textMuted text-sm mt-4 hover:text-white">Continue Shopping</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
